import React, {useCallback} from "react";
import {TASK_STATUS_ENUM} from "constraints/taskd";
import {IStandTaskListRow} from "ts/api/stand-task-list-row";
import {useSnackbar} from "notistack";
import {useTranslate} from "@tolgee/react";
import CloseSnackbarButton from "view/components/Snackbar/notistack/CloseButton";
import caseInsensitiveSort from "tools/common/caseInsensitiveSort";
import {useTheme} from "@material-ui/core";
import {IOrganiser, TaskStatus} from "@evennt-tech/api-v2-core";

export const getStatus = (row?: IStandTaskListRow): string => {
  if (row?.instance?.isRejected) {
    return "Rejected";
  }
  if (row?.instance?.isInRevision) {
    return TASK_STATUS_ENUM.revision;
  }
  const status = row?.instance?.status ?? TaskStatus.TODO;
  // Draft = response started but not submitted; the instance stays in
  // TODO, so surface the underlying status alongside the draft marker.
  if (row?.isDraft && status === TaskStatus.TODO) {
    return `${TASK_STATUS_ENUM[TaskStatus.TODO]} (Draft)`;
  }
  return TASK_STATUS_ENUM[status];
};

export const getStatusTextColor = (row?: IStandTaskListRow): string => {
  const theme = useTheme();
  const status = row?.instance?.status;
  if (row?.instance?.isRejected) return theme.palette.error.main;
  if (row?.isDraft && (status ?? TaskStatus.TODO) === TaskStatus.TODO)
    return theme.palette.text.secondary;
  if (status === TaskStatus.WAITING_FOR_APPROVAL)
    return theme.palette.warning.main;
  if (status === TaskStatus.IN_REVIEW) return theme.palette.warning.main;
  if (status === TaskStatus.DONE) return theme.palette.success.main;
  return theme.palette.contrastText.main;
};

export const useGetShowSubmittedMessage = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {t} = useTranslate();
  return useCallback(() => {
    enqueueSnackbar(t("common.tasks.response_modal.submitted_success"), {
      variant: "success",
      action: (snackbarId) => (
        <CloseSnackbarButton onClick={() => closeSnackbar(snackbarId)} />
      ),
    });
  }, [enqueueSnackbar, closeSnackbar, t]);
};

const mapToOrganiserOption = (organiser: IOrganiser) => ({
  label: organiser.contactPerson,
  value: organiser.id,
  company:
    organiser?.organiserCompanyToOrganisers?.[0]?.organiserCompany?.name ||
    null,
});

export const getOrganiserOptions = (organisers: IOrganiser[]) => {
  const options = organisers.map(mapToOrganiserOption);
  return caseInsensitiveSort(options, "contactPerson");
};
